import { useTranslation } from "react-i18next";
import { FaCheck } from "react-icons/fa6";
import { ImSpinner2 } from "react-icons/im";
import { css } from "../../../../styled-system/css";
import { Box, HStack, VStack } from "../../../../styled-system/jsx";
import type { IngestionPhase, PhaseKey, PhaseStatus } from "../types";

const dot: Record<PhaseStatus, { bg: string; fg: string; border: string }> = {
  done: { bg: "green.600/15", fg: "green.700", border: "green.600" },
  running: { bg: "white", fg: "royalPurple", border: "royalPurple" },
  queued: { bg: "appBackground", fg: "text.secondary", border: "border.default" },
  failed: { bg: "white", fg: "error", border: "error" },
};

const connectorColor: Record<PhaseStatus, string> = {
  done: "green.600",
  running: "royalPurple/40",
  queued: "border.default",
  failed: "error/40",
};

export const IngestionStepper = ({ phases }: { phases: IngestionPhase[] }) => {
  const { t } = useTranslation(["onboarding"]);

  const phaseLabel: Record<PhaseKey, string> = {
    recent: t(($) => $.onboarding["Recent performance"]),
    backfill: t(($) => $.onboarding["2-year history"]),
    enrichment: t(($) => $.onboarding["Enrichment"]),
  };

  const statusText: Record<PhaseStatus, string> = {
    done: t(($) => $.onboarding["Complete"]),
    running: t(($) => $.onboarding["In progress"]),
    queued: t(($) => $.onboarding["Queued"]),
    failed: t(($) => $.onboarding["Failed"]),
  };

  return (
    <HStack alignItems="flex-start" gap="0" width="100%">
      {phases.map((phase, i) => {
        return (
          <VStack key={phase.key} flex="1" gap="0" position="relative" alignItems="center" textAlign="center">
            {i > 0 && (
              <Box
                className={css({ position: "absolute", top: "15px", height: "2px" })}
                style={{ left: "calc(-50% + 17px)", right: "calc(50% + 17px)" }}
                backgroundColor={connectorColor[phase.status]}
              />
            )}
            <Box
              position="relative"
              zIndex="1"
              width="32px"
              height="32px"
              borderRadius="50%"
              display="flex"
              alignItems="center"
              justifyContent="center"
              border="2px solid"
              backgroundColor={dot[phase.status].bg}
              color={dot[phase.status].fg}
              borderColor={dot[phase.status].border}
            >
              {phase.status === "done" && <FaCheck size={15} />}
              {phase.status === "running" && <ImSpinner2 size={15} className={css({ animation: "spin" })} />}
              {(phase.status === "queued" || phase.status === "failed") && (
                <Box width="7px" height="7px" borderRadius="50%" backgroundColor="currentcolor" />
              )}
            </Box>
            <Box marginTop="9px" fontSize="small" fontWeight="bold" color="text.heading">
              {phaseLabel[phase.key]}
            </Box>
            <Box marginTop="2px" fontSize="small" fontWeight="semibold" color={dot[phase.status].border}>
              {statusText[phase.status]}
            </Box>
          </VStack>
        );
      })}
    </HStack>
  );
};
